import React, { useCallback, useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { PositioningPortal } from '@move/react-positioning-portal';
import { ButtonSize, GhostInverseButton } from '@move-ui/button';
import { CloseIcon } from '@move-ui/icons-asset-catalog';
import { Direction, getTooltipPosition } from './utils/getTooltipPosition';
import { DefaultTooltipContent, TooltipMode, TooltipSize } from './TooltipContent';
import styles from './Tooltip.module.css';
export var TriggerMode;
(function (TriggerMode) {
    TriggerMode["Manual"] = "manual";
    TriggerMode["Event"] = "event";
    TriggerMode["Timer"] = "timer";
})(TriggerMode || (TriggerMode = {}));
const TIMER_MODE_MS = 6000;
const HIDE_TOOLTIP_ANIMATION_DELAY_MS = 100;
const Tooltip = ({ children, tooltipId, className, direction = Direction.Bottom, headline, headlineIcon, contentText, tooltipSize = TooltipSize.Large, tooltipMode = TooltipMode.Dark, customContent, disableTooltip, triggerMode = TriggerMode.Event, portalElement, closeButtonLabel, closeOnOutsideClick = true, timerTimeVisibleInMs = TIMER_MODE_MS, onOpen = () => { }, onClose = () => { } }) => {
    const [isTooltipVisible, setTooltipVisible] = useState(false);
    const [isHidden, setHidden] = useState(true);
    const hasTimerModeBeenShown = useRef(false);
    const isEventModeEnabled = triggerMode === TriggerMode.Event;
    const isTimerModeEnabled = triggerMode === TriggerMode.Timer;
    const timeout = useRef();
    const showTooltip = useCallback(() => {
        setTooltipVisible(true);
        onOpen();
        if (timeout.current) {
            clearTimeout(timeout.current);
        }
        timeout.current = setTimeout(() => {
            setHidden(false);
            hasTimerModeBeenShown.current = true;
        });
    }, [onOpen]);
    const hideTooltip = useCallback(() => {
        setHidden(true);
        if (timeout.current) {
            clearTimeout(timeout.current);
        }
        timeout.current = setTimeout(() => {
            setTooltipVisible(false);
            onClose();
        }, HIDE_TOOLTIP_ANIMATION_DELAY_MS);
    }, [onClose]);
    const buildPointerStyle = useCallback((strategy) => ({
        transform: `${['left', 'right'].includes(strategy === null || strategy === void 0 ? void 0 : strategy.position)
            ? 'translateY'
            : 'translateX'}(${(strategy === null || strategy === void 0 ? void 0 : strategy.shift) || 0}px)`
    }), []);
    const tooltipTrigger = React.cloneElement(React.Children.only(children), {
        onMouseEnter: !isEventModeEnabled ? () => { } : showTooltip,
        onMouseLeave: !isEventModeEnabled ? () => { } : hideTooltip,
        onFocus: !isEventModeEnabled ? () => { } : showTooltip,
        onBlur: !isEventModeEnabled ? () => { } : hideTooltip,
        onClick: isEventModeEnabled ? () => { } : showTooltip,
        id: tooltipId
    });
    // handles timer mode
    useEffect(() => {
        if (isTimerModeEnabled && !hasTimerModeBeenShown.current) {
            showTooltip();
            setTimeout(() => {
                hideTooltip();
            }, timerTimeVisibleInMs);
        }
        return () => timeout.current && clearTimeout(timeout.current);
    }, [
        hasTimerModeBeenShown,
        hideTooltip,
        isTimerModeEnabled,
        showTooltip,
        timerTimeVisibleInMs
    ]);
    return (React.createElement("div", { className: styles.component },
        React.createElement(PositioningPortal, { isOpen: isTooltipVisible && !disableTooltip, closeOnOutsideClick: closeOnOutsideClick, onShouldClose: hideTooltip, portalElement: portalElement, positionStrategy: getTooltipPosition(direction), closeOnScroll: !isTimerModeEnabled, portalContent: ({ strategy, isPositioned }) => (React.createElement("div", { className: cx(styles.tooltipContainer, {
                    [styles.top]: (strategy === null || strategy === void 0 ? void 0 : strategy.position) === Direction.Top,
                    [styles.left]: (strategy === null || strategy === void 0 ? void 0 : strategy.position) === Direction.Left,
                    [styles.right]: (strategy === null || strategy === void 0 ? void 0 : strategy.position) === Direction.Right,
                    [styles.light]: tooltipMode === TooltipMode.Light,
                    [styles.small]: tooltipSize === TooltipSize.Small,
                    [styles.hidden]: isHidden
                }, className), role: "tooltip", "aria-describedby": tooltipId, "aria-hidden": !isPositioned },
                !isEventModeEnabled && !isTimerModeEnabled && (React.createElement(GhostInverseButton, { size: ButtonSize.Size100, className: styles.closeButton, iconOnly: true, icon: React.createElement(CloseIcon, { className: styles.closeIcon }), onClick: hideTooltip, label: closeButtonLabel })),
                customContent || (React.createElement(React.Fragment, null,
                    React.createElement(DefaultTooltipContent, { headline: headline, headlineIcon: headlineIcon, contentText: contentText, tooltipMode: tooltipMode, tooltipSize: tooltipSize }))),
                React.createElement("div", { className: styles.pointerOutline, style: buildPointerStyle(strategy) }),
                React.createElement("div", { className: styles.pointer, style: buildPointerStyle(strategy) }))) }, tooltipTrigger)));
};
export default Tooltip;
//# sourceMappingURL=Tooltip.js.map